import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useSubmissionTemplateForm } from '@/hooks/submission_forms/useSubmissionTemplateForm';
import { useSubmissionForm } from '@/hooks/submission_forms/useSubmissionForm';
import { DynamicForm } from '@/features/submission_forms';
import FullPageHeader from '@/components/FullPageHeader';
import { useSubmissionAnswers } from '@/hooks/submissions/useSubmissionAnswers';
import { useCompleteClarifications } from '@/hooks/submissions/useCompleteClarifications';
import { ClarificationsProvider } from '@/contexts/ClarificationsContext';
import { DebugRoleProvider } from '@/contexts/DebugRoleContext';
import DebugRolePanel from '@/components/debug/DebugRolePanel';

const SubmissionForm: React.FC = () => {
  const { submissionId, id } = useParams<{
    submissionId?: string;
    id?: string;
  }>();
  const [searchParams] = useSearchParams();

  // Two modes: creating from template OR viewing/editing existing submission
  const isExistingSubmission = !!submissionId;
  const templateId = id; // For creating new submissions from template

  // Determine form mode from query param and permission
  // Default is 'read' unless ?mode=edit is specified and user has permission
  const modeParam = searchParams.get('mode');

  const { fetchSubmissionForm, submissionFormData } =
    useSubmissionForm(submissionId);
  const { fetchTemplateForm, templateFormData } = useSubmissionTemplateForm(
    templateId || submissionFormData?.submission_template_id
  );
  const { answers, isLoading: isLoadingAnswers } = useSubmissionAnswers(
    submissionId || ''
  );

  // Read-only if: existing submission AND (no data yet OR no permission OR not in edit mode)
  const isReadOnly =
    isExistingSubmission &&
    (!submissionFormData ||
      !submissionFormData.can_edit ||
      modeParam !== 'edit');

  // OV-1180: a combined Modification is built from several templates, but
  // templateFormData is only ever the primary one — naming its sub-category alone
  // would present one of several as though it were the whole submission. Prefer the
  // submission's own list, which the blueprint returns primary-first.
  const subCategories = submissionFormData?.sub_categories?.length
    ? submissionFormData.sub_categories
    : [templateFormData?.sub_category].filter(Boolean);

  const templateLabel = [
    templateFormData?.category,
    subCategories.join(', '),
    // A variant belongs to a single sub-category, so it only reads correctly when
    // there is one. Combined submissions show their variants on the review step.
    subCategories.length > 1 ? null : templateFormData?.variant,
  ]
    .filter(Boolean)
    .join(' — ');
  const headerTitle = !isExistingSubmission
    ? templateLabel
      ? `New Submission — ${templateLabel}`
      : 'New Submission'
    : isReadOnly
      ? templateLabel
        ? `View Submission — ${templateLabel}`
        : 'View Submission'
      : templateLabel
        ? `Edit Submission — ${templateLabel}`
        : 'Edit Submission';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onClose = () => {
    if (submissionId) {
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      // Invalidate site/sites caches — saving a submission can update mapped
      // fields that flow into the site and study details panels.
      queryClient.invalidateQueries({ queryKey: ['site'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate(`/submissions/${submissionId}`);
    } else {
      navigate('/submissions');
    }
  };

  // Existing submission: fetch submission data first, then template
  useEffect(() => {
    if (isExistingSubmission && submissionId) {
      fetchSubmissionForm();
    }
  }, [submissionId, isExistingSubmission, fetchSubmissionForm]); // Only depend on submissionId, not fetchSubmissionForm

  // Create mode: fetch template directly, Existing: fetch after submission data loads
  useEffect(() => {
    if (templateId || submissionFormData?.submission_template_id) {
      fetchTemplateForm();
    }
  }, [
    fetchTemplateForm,
    templateId,
    submissionFormData?.submission_template_id,
  ]);

  // For an existing submission, always use its own frozen question_tree
  // snapshot — never the live template's. A submission's template can gain
  // new published versions (different questions/slugs) long after the
  // submission was created; falling back to the live template here would
  // render questions the submission was never actually snapshotted with,
  // so saving an answer or requesting a clarification against them 404s.
  // Only a brand-new submission (no submissionId yet) has no snapshot of
  // its own, so it legitimately previews the template's current tree.
  const formTree = isExistingSubmission
    ? submissionFormData?.question_tree
    : templateFormData?.question_tree;

  return (
    <DebugRoleProvider>
      <ClarificationsProvider
        submissionId={submissionId}
        submissionStatus={submissionFormData?.status}
      >
        <div className="bg-gray-50 flex flex-col h-full overflow-x-hidden">
          <SubmissionHeader
            title={headerTitle}
            onClose={onClose}
            submissionId={submissionId}
            submissionStatus={submissionFormData?.status}
            refreshSubmissionForm={fetchSubmissionForm}
          />

          {/* Content */}
          <div className="overflow-x-hidden pt-[72px]">
            {/* Form Content */}
            {formTree &&
            formTree.length > 0 &&
            (!isExistingSubmission || !isLoadingAnswers) ? (
              <DynamicForm
                formTree={formTree}
                savedAnswers={answers}
                readOnly={isReadOnly}
              />
            ) : templateFormData ? (
              <div className="text-center py-16">
                <div className="mx-auto max-w-md">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No questions available
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This form template doesn&apos;t have any questions
                    configured yet.
                  </p>
                </div>
              </div>
            ) : isExistingSubmission &&
              submissionFormData &&
              !submissionFormData.submission_template_id ? (
              <div className="text-center py-16">
                <div className="mx-auto max-w-md">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No form available
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This submission was imported from a previous system and does
                    not have a form associated with it. Any answers on record
                    are shown in the submission details.
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Debug panel - only renders in development */}
        <DebugRolePanel />
      </ClarificationsProvider>
    </DebugRoleProvider>
  );
};

/**
 * Thin wrapper around FullPageHeader that calls useCompleteClarifications
 * inside the ClarificationsProvider / DebugRoleProvider context tree.
 */
const SubmissionHeader: React.FC<{
  title: string;
  onClose: () => void;
  submissionId: string | undefined;
  submissionStatus: string | undefined;
  refreshSubmissionForm: () => Promise<void>;
}> = ({
  title,
  onClose,
  submissionId,
  submissionStatus,
  refreshSubmissionForm,
}) => {
  const {
    showCompleteClarifications,
    canComplete,
    handleCompleteClarifications,
    isCompleting,
    completeError,
  } = useCompleteClarifications({
    submissionId,
    refreshSubmissionForm,
  });

  return (
    <FullPageHeader
      title={title}
      onClose={onClose}
      showDownload={!!submissionId}
      showCurrentDownload={submissionStatus === 'draft'}
      showDone={showCompleteClarifications}
      onDone={handleCompleteClarifications}
      isDoneLoading={isCompleting}
      isDoneDisabled={!canComplete}
      doneLabel="Complete Clarifications"
      doneError={completeError}
    />
  );
};

export default SubmissionForm;
